import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { socket } from '../Containers/MainContainer';
import { SkeletonTheme } from 'react-loading-skeleton';

// Utility functions
const numberWithCommas = (num) => num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// Skeleton Loading Theme Component
const Skeleton = ({ className = "", style = {} }) => {
  return (
    <div
      className={className}
      style={{
        background: 'linear-gradient(90deg, #161b22 25%, #21262d 50%, #161b22 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite',
        borderRadius: '4px',
        ...style
      }}
    ></div>
  );
};

// Shimmer keyframes injected once
const shimmerStyle = document.createElement('style');
if (!document.getElementById('pluto-shimmer-style')) {
  shimmerStyle.id = 'pluto-shimmer-style';
  shimmerStyle.textContent = `
    @keyframes shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
  `;
  document.head.appendChild(shimmerStyle);
}

// Enhanced Statistics Component with skeleton support
const StatCard = ({ title, value, subtitle, trend, trendValue, color = "blue", isLoading = false, icon }) => {
  const colorMap = {
    blue: { borderLeft: '3px solid #58a6ff' },
    green: { borderLeft: '3px solid #3fb950' },
    yellow: { borderLeft: '3px solid #d29922' },
    red: { borderLeft: '3px solid #f85149' },
    purple: { borderLeft: '3px solid #a855f7' },
    orange: { borderLeft: '3px solid #f97316' }
  };

  return (
    <div style={{
      background: '#161b22',
      border: '1px solid #21262d',
      borderRadius: '12px',
      padding: '20px',
      transition: 'all 0.3s',
      ...colorMap[color]
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            {icon && <span style={{ fontSize: '18px' }}>{icon}</span>}
            <p style={{ color: '#8b949e', fontSize: '11px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.8px', margin: 0 }}>{title}</p>
          </div>
          {isLoading ? (
            <Skeleton style={{ width: '80px', height: '28px', marginBottom: '4px' }} />
          ) : (
            <p style={{ fontSize: '24px', fontWeight: 700, color: '#e6edf3', marginBottom: '4px', margin: 0 }}>{value}</p>
          )}
          {subtitle && (
            isLoading ? (
              <Skeleton style={{ width: '64px', height: '16px' }} />
            ) : (
              <p style={{ color: '#8b949e', fontSize: '12px', margin: 0 }}>{subtitle}</p>
            )
          )}
        </div>
        {trend && !isLoading && (
          <div style={{
            fontSize: '12px',
            fontWeight: 500,
            padding: '4px 8px',
            borderRadius: '9999px',
            color: trend === 'up' ? '#3fb950' : '#f85149',
            background: trend === 'up' ? 'rgba(63,185,80,0.15)' : 'rgba(248,81,73,0.15)'
          }}>
            <span style={{ fontSize: '14px' }}>{trend === 'up' ? '↗' : '↘'}</span>
            <span style={{ marginLeft: '4px' }}>{trendValue}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const Modal = ({ show, title, onConfirm, onCancel, confirmText = "Confirm", cancelText = "Cancel", children }) => {
  if (!show) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 50, padding: '16px'
    }}>
      <div style={{
        background: '#161b22', borderRadius: '12px',
        border: '1px solid #30363d', width: '100%', maxWidth: '448px'
      }}>
        <div style={{ padding: '24px' }}>
          {title && <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#e6edf3', marginBottom: '16px' }}>{title}</h3>}
          <div style={{ color: '#c9d1d9', marginBottom: '24px' }}>{children}</div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <button
              onClick={onCancel}
              style={{
                padding: '8px 16px', background: '#21262d', border: '1px solid #30363d',
                color: '#e6edf3', borderRadius: '8px', cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#30363d'}
              onMouseLeave={(e) => e.target.style.background = '#21262d'}
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              style={{
                padding: '8px 16px', background: '#238636', border: '1px solid #2ea043',
                color: '#ffffff', borderRadius: '8px', cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#2ea043'}
              onMouseLeave={(e) => e.target.style.background = '#238636'}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Button = ({ variant = "primary", size = "md", onClick, children, className = "", disabled = false, style: extraStyle = {} }) => {
  const variants = {
    primary: { background: '#238636', border: '1px solid #2ea043', color: '#ffffff' },
    secondary: { background: '#21262d', border: '1px solid #30363d', color: '#e6edf3' },
    success: { background: '#238636', border: '1px solid #2ea043', color: '#ffffff' },
    danger: { background: '#da3633', border: '1px solid #f85149', color: '#ffffff' },
    warning: { background: '#9e6a03', border: '1px solid #d29922', color: '#ffffff' },
    brand: { background: '#238636', border: '1px solid #2ea043', color: '#ffffff' },
    disabled: { background: '#21262d', border: '1px solid #30363d', color: '#8b949e' }
  };

  const sizes = {
    sm: { padding: '6px 12px', fontSize: '12px' },
    md: { padding: '8px 16px', fontSize: '14px' },
    lg: { padding: '12px 24px', fontSize: '16px' }
  };

  const variantStyle = variants[variant] || variants.primary;
  const sizeStyle = sizes[size] || sizes.md;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...variantStyle,
        ...sizeStyle,
        borderRadius: '8px',
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s',
        ...extraStyle
      }}
    >
      {children}
    </button>
  );
};

const Input = ({ type = "text", placeholder, value, onChange, className = "", id, style: extraStyle = {} }) => (
  <input
    type={type}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    id={id}
    style={{
      padding: '8px 16px',
      background: '#0d1117',
      border: '1px solid #30363d',
      borderRadius: '8px',
      color: '#e6edf3',
      fontSize: '14px',
      outline: 'none',
      transition: 'border-color 0.2s',
      ...extraStyle
    }}
    onFocus={(e) => e.target.style.borderColor = '#58a6ff'}
    onBlur={(e) => e.target.style.borderColor = '#30363d'}
  />
);

// Enhanced Table with skeleton rows
const Table = ({ headers, children, isLoading = false, skeletonRows = 5 }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: '#161b22' }}>
          {headers.map((header, index) => (
            <th key={index} style={{
              padding: '12px 16px', textAlign: 'left', fontSize: '11px',
              fontWeight: 500, color: '#8b949e', textTransform: 'uppercase',
              letterSpacing: '0.8px', borderBottom: '1px solid #21262d'
            }}>
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {isLoading ? (
          Array.from({ length: skeletonRows }, (_, index) => (
            <tr key={index} style={{ borderBottom: '1px solid #21262d' }}>
              {headers.map((_, cellIndex) => (
                <td key={cellIndex} style={{ padding: '16px' }}>
                  <Skeleton style={{ width: '100%', height: '16px' }} />
                </td>
              ))}
            </tr>
          ))
        ) : children}
      </tbody>
    </table>
  </div>
);

const Card = ({ title, children, headerActions, isLoading = false }) => (
  <div style={{
    background: '#161b22',
    border: '1px solid #21262d',
    borderRadius: '12px',
    overflow: 'hidden'
  }}>
    {title && (
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #21262d',
        background: '#161b22'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#e6edf3', margin: 0 }}>{title}</h3>
          {headerActions}
        </div>
      </div>
    )}
    <div style={{ padding: '16px' }}>{children}</div>
  </div>
);

// Pagination Component
const PaginationPluto = ({ pageNumber, currentPage = 1, requestNewList }) => {
  const startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(pageNumber, startPage + 4);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
      <Button variant="secondary" size="sm" onClick={() => requestNewList(1)} disabled={currentPage === 1}>
        First
      </Button>
      <Button variant="secondary" size="sm" onClick={() => requestNewList(currentPage - 1)} disabled={currentPage === 1}>
        Prev
      </Button>

      {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(page => (
        <Button
          key={page}
          variant={page === currentPage ? "primary" : "secondary"}
          size="sm"
          onClick={() => requestNewList(page)}
          style={{ minWidth: '40px' }}
        >
          {page}
        </Button>
      ))}

      <Button variant="secondary" size="sm" onClick={() => requestNewList(currentPage + 1)} disabled={currentPage === pageNumber}>
        Next
      </Button>
      <Button variant="secondary" size="sm" onClick={() => requestNewList(pageNumber)} disabled={currentPage === pageNumber}>
        Last
      </Button>
    </div>
  );
};

const SuspectOffersDashboard = () => {
  // State management - ALL useState hooks at the top level, same order every render
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Data states from original
  const [stats, setStats] = useState({
    offers: 0,
    offersToday: 0,
    pending: 0,
    chargebacks: 0,
    speedyRules: 0,
    pendingAmount: 0,
    chargebackAmount: 0
  });

  const [tableData, setTableData] = useState({
    offers: [],
    pending: [],
    chargebacks: []
  });

  const [pageNumbers, setPageNumbers] = useState({
    offers: 0,
    pending: 0,
    chargebacks: 0
  });

  const [chartData, setChartData] = useState({
    monthly: [],
    walls: [],
    chargebackTrends: [],
    wallPerformance: []
  });

  // Modal states
  const [modals, setModals] = useState({
    release: { show: false, data: {} }
  });

  // Socket event handlers
  useEffect(() => {
    const handlers = {
      'response_get_admin_offers': (data) => {
        setTableData(prev => ({ ...prev, offers: data.withdrawals }));
        setPageNumbers(prev => ({ ...prev, offers: Math.ceil(data.withdrawalsLength / 5) }));
        setStats(prev => ({ ...prev, offers: data.withdrawalsLength }));
        setLoading(false);
      },
      'response_get_admin_offers_today': (data) => {
        setStats(prev => ({ ...prev, offersToday: data.withdrawalsLength }));
      },
      'response_get_admin_pending': (data) => {
        setTableData(prev => ({ ...prev, pending: data.withdrawals }));
        setPageNumbers(prev => ({ ...prev, pending: Math.ceil(data.withdrawalsLength / 5) }));
        setStats(prev => ({
          ...prev,
          pending: data.withdrawalsLength,
          pendingAmount: data.pendingAmount
        }));
      },
      'response_get_admin_chargeback': (data) => {
        setTableData(prev => ({ ...prev, chargebacks: data.withdrawals }));
        setPageNumbers(prev => ({ ...prev, chargebacks: Math.ceil(data.withdrawalsLength / 10) }));
        setStats(prev => ({
          ...prev,
          chargebacks: data.withdrawalsLength,
          chargebackAmount: data.cbAmount
        }));
        setChartData(prev => ({
          ...prev,
          monthly: data.dataMonthCB,
          walls: data.wallChargebackData,
          chargebackTrends: generateChargebackTrends(),
          wallPerformance: generateWallPerformance()
        }));
      },
      'response_get_admin_offers_fast_c': (data) => {
        setStats(prev => ({ ...prev, speedyRules: data.withdrawalsLength }));
      }
    };

    // Register event handlers
    Object.entries(handlers).forEach(([event, handler]) => {
      socket.on(event, handler);
    });

    // Initial data fetch - only suspect offers
    socket.emit('getOffersAdmin', { offers_like: '', page: 1 });
    socket.emit('getOffersAdminToday', { offers_like: '', type: 0, page: 1 });
    socket.emit('getPendingOffersAdmin', { page: 1 });
    socket.emit('getChargebacksAdmin', { offers_like: '', page: 1, groupped: true, type: 0 });
    socket.emit('getOffersAdminFastC', { offers_like: '', page: 1 });

    return () => {
      // Cleanup event handlers
      Object.keys(handlers).forEach(event => {
        socket.off(event);
      });
    };
  }, []);

  // Generate additional chart data
  const generateChargebackTrends = () => [
    { day: 'Mon', amount: 120, count: 5 },
    { day: 'Tue', amount: 95, count: 3 },
    { day: 'Wed', amount: 180, count: 8 },
    { day: 'Thu', amount: 75, count: 2 },
    { day: 'Fri', amount: 220, count: 12 },
    { day: 'Sat', amount: 140, count: 6 },
    { day: 'Sun', amount: 85, count: 4 }
  ];

  const generateWallPerformance = () => [
    { wall: 'AdGate', revenue: 2840, chargebacks: 145, ratio: 5.1 },
    { wall: 'OfferToro', revenue: 2150, chargebacks: 89, ratio: 4.1 },
    { wall: 'RevU', revenue: 1920, chargebacks: 112, ratio: 5.8 },
    { wall: 'AdGem', revenue: 1650, chargebacks: 67, ratio: 4.1 }
  ];

  // Helper functions
  const getCbRateStyle = (rate) => {
    const numRate = parseInt(rate);
    if (numRate <= 10) return { background: 'rgba(210,153,34,0.15)', color: '#d29922' };
    if (numRate <= 20) return { background: 'rgba(210,153,34,0.25)', color: '#d29922' };
    if (numRate <= 40) return { background: 'rgba(248,81,73,0.15)', color: '#f85149' };
    return { background: 'rgba(248,81,73,0.25)', color: '#f85149' };
  };

  const getCategoryStyle = (category) => {
    if (category == 'apps') return { background: 'rgba(210,153,34,0.15)', color: '#d29922' };
    if (category == 'games') return { background: 'rgba(63,185,80,0.15)', color: '#3fb950' };
    if (category == 'videos') return { background: 'rgba(88,166,255,0.15)', color: '#58a6ff' };
    if (category == 'surveys') return { background: 'rgba(168,85,247,0.15)', color: '#a855f7' };
    if (category == 'signup') return { background: 'rgba(88,166,255,0.2)', color: '#58a6ff' };
    if (category == 'clicks') return { background: 'rgba(139,148,158,0.15)', color: '#8b949e' };
    if (category == 'cashback') return { background: 'rgba(20,184,166,0.15)', color: '#14b8a6' };
    if (category == 'gambling') return { background: 'rgba(249,115,22,0.15)', color: '#f97316' };
    if (category == 'gpt') return { background: 'rgba(248,81,73,0.15)', color: '#f85149' };
    return { background: 'rgba(139,148,158,0.1)', color: '#8b949e' };
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
    setTableData(prev => ({ ...prev, offers: new Array(5).fill({}) }));
    socket.emit('getOffersAdmin', { offers_like: value.toLowerCase(), page: 1 });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setTableData(prev => ({ ...prev, offers: new Array(5).fill({}) }));
    socket.emit('getOffersAdmin', { offers_like: searchTerm, page });
  };

  const openModal = (type, data = {}) => {
    setModals(prev => ({
      ...prev,
      [type]: { show: true, data }
    }));
  };

  const closeModal = (type) => {
    setModals(prev => ({
      ...prev,
      [type]: { show: false, data: {} }
    }));
  };

  // Advanced analytics data (mock for enhanced visuals)
  const riskAnalytics = [
    { category: 'Speed', high: Math.min(stats.offers * 0.4, 45), medium: Math.min(stats.offers * 0.6, 78), low: Math.min(stats.offers * 0.2, 23) },
    { category: 'Pattern', high: Math.min(stats.chargebacks * 0.3, 67), medium: Math.min(stats.chargebacks * 0.4, 45), low: Math.min(stats.chargebacks * 0.1, 12) },
    { category: 'Location', high: Math.min(stats.pending * 0.2, 23), medium: Math.min(stats.pending * 0.8, 89), low: Math.min(stats.pending * 0.6, 67) },
    { category: 'Timing', high: Math.min(stats.speedyRules * 0.5, 34), medium: Math.min(stats.speedyRules * 0.7, 56), low: Math.min(stats.speedyRules * 0.9, 78) }
  ];

  const wallComparison = chartData.walls?.map(wall => ({
    wall: wall.wall || 'Unknown',
    suspectRate: Math.random() * 15,
    totalOffers: Math.floor(Math.random() * 1000) + 500,
    suspects: wall.amount ? parseInt(wall.amount.replace('$', '')) / 10 : 0
  })) || [];

  return (
     <SkeletonTheme baseColor="#161b22" highlightColor="#21262d">
    <div style={{ minHeight: '100vh', background: '#0d1117' }}>
      {/* Enhanced Header */}
      <header style={{
        background: '#161b22',
        borderBottom: '1px solid #21262d',
        padding: '16px 24px',
        position: 'sticky',
        top: 0,
        zIndex: 50
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '32px', height: '32px',
              background: 'linear-gradient(135deg, #f85149, #f97316)',
              borderRadius: '8px',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <span style={{ color: '#ffffff', fontWeight: 700, fontSize: '14px' }}>!</span>
            </div>
            <div>
              <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#e6edf3', margin: 0 }}>Offers Dashboard</h1>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ fontSize: '12px', color: '#8b949e' }}>
              Last updated: {new Date().toLocaleTimeString()}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '8px', height: '8px',
                background: '#3fb950', borderRadius: '50%',
                animation: 'pulse 2s infinite'
              }}></div>
              <span style={{ color: '#3fb950', fontSize: '14px', fontWeight: 500 }}>Live Monitoring</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '32px' }}>

        {/* Suspect Offers Table */}
        <Card
          title="Recent Offers"
          headerActions={
            <div style={{ display: 'flex', gap: '8px' }}>
              <Input
                placeholder="Search offers..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ width: '256px' }}
              />
              <Button variant="primary" size="sm" onClick={() => socket.emit('getOffersAdmin', { offers_like: '', page: 1 })}>
                Refresh
              </Button>
            </div>
          }
          isLoading={loading}
        >
          <Table
            headers={['Name', 'Category', 'Amount', 'Payout', 'Wall', 'Date', 'Country', 'CB Rate', 'Action']}
            isLoading={loading}
            skeletonRows={8}
          >
            {tableData.offers.map((offer, index) => (
              <tr key={offer.id || index} style={{
                borderBottom: '1px solid #21262d',
                transition: 'background 0.2s'
              }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(22,27,34,0.5)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#e6edf3' }}>
                  {offer.name || <Skeleton style={{ width: '128px', height: '16px' }} />}
                </td>
                <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px' }}>
                  {offer.category ? (
                    <span style={{
                      padding: '4px 8px', borderRadius: '9999px',
                      fontSize: '12px', fontWeight: 500,
                      ...getCategoryStyle(offer.category)
                    }}>
                      {offer.category}
                    </span>
                  ) : (
                    <Skeleton style={{ width: '48px', height: '20px' }} />
                  )}
                </td>
                <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#3fb950', fontWeight: 500 }}>
                  {offer.amount || <Skeleton style={{ width: '64px', height: '16px' }} />}
                </td>
                <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#58a6ff', fontWeight: 500 }}>
                  {offer.payout || <Skeleton style={{ width: '64px', height: '16px' }} />}
                </td>
                <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#c9d1d9' }}>
                  {offer.wall || <Skeleton style={{ width: '80px', height: '16px' }} />}
                </td>
                <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#8b949e' }}>
                  {offer.date || <Skeleton style={{ width: '80px', height: '16px' }} />}
                </td>
                <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px', color: '#c9d1d9' }}>
                  {offer.country || <Skeleton style={{ width: '32px', height: '16px' }} />}
                </td>
                <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px' }}>
                  {offer.cb_rate ? (
                    <span style={{
                      padding: '4px 8px', borderRadius: '9999px',
                      fontSize: '12px', fontWeight: 500,
                      ...getCbRateStyle(offer.cb_rate)
                    }}>
                      {offer.cb_rate}%
                    </span>
                  ) : (
                    <Skeleton style={{ width: '48px', height: '20px' }} />
                  )}
                </td>

                <td style={{ padding: '16px', whiteSpace: 'nowrap', fontSize: '14px' }}>
                  {offer.id ? (
                    <Button variant="secondary" size="sm" onClick={() => window.open(`/pluto/user/${offer.user_id}`, '_blank')}>
                      Check User
                    </Button>
                  ) : (
                    <Skeleton style={{ width: '80px', height: '24px' }} />
                  )}
                </td>
              </tr>
            ))}
          </Table>

          <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <PaginationPluto
              pageNumber={pageNumbers.offers}
              currentPage={currentPage}
              requestNewList={handlePageChange}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Input
                type="number"
                placeholder="Page"
                style={{ width: '80px' }}
                id="jumpToPageOffers"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const page = document.getElementById('jumpToPageOffers').value;
                  if (page && page > 0 && page <= pageNumbers.offers) {
                    handlePageChange(parseInt(page));
                  }
                }}
              >
                Go
              </Button>
            </div>
          </div>
        </Card>


      </main>

      {/* Modals */}
      <Modal
        show={modals.release.show}
        title="Release Pending Offer"
        onConfirm={() => {
          const { user_id, offer_id } = modals.release.data;
          socket.emit('adminAction', {
            action: 'releaseOfferAdmin',
            user_id,
            offer_id,
          });
          closeModal('release');
          // Refresh pending offers
          socket.emit('getPendingOffersAdmin', { page: 1 });
        }}
        onCancel={() => closeModal('release')}
        confirmText="Release"
        cancelText="Cancel"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <p style={{ margin: 0 }}><strong style={{ color: '#e6edf3' }}>Offer:</strong> {modals.release.data.name}</p>
          <p style={{ margin: 0 }}><strong style={{ color: '#e6edf3' }}>Offer ID:</strong> {modals.release.data.offer_id}</p>
          <p style={{ margin: 0 }}><strong style={{ color: '#e6edf3' }}>User ID:</strong> {modals.release.data.user_id}</p>
          <p style={{ color: '#d29922', fontSize: '12px', marginTop: '16px' }}>
            This will immediately release the pending offer to the user.
          </p>
        </div>
      </Modal>
    </div>
    </SkeletonTheme>
  );
};

export default SuspectOffersDashboard;
